/**
 * The attempt: ONE run on the case file, folded to one line.
 *
 * It answers "what did this attempt do" for the person deciding what happens
 * next: which steps it built, what it kept, whether it opened a pull request,
 * and what it cost. Collapsed, all of that is the summary line; opened, the
 * steps, what it produced, and its log, which is THIS attempt's events and no
 * other run's (`useRunEvents({ runId })`). A scoping memo written an hour
 * earlier is not part of the build that stopped.
 *
 * Built from the live stream's reads, so the two never disagree: the run view
 * for the steps and the kept candidate (only the view joins `run_artifacts`),
 * the coding events for the commits and the log. The run view names the
 * latest execution run, so its steps and outputs count only when it names
 * this one.
 */
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { WorkItemRunListEntry, WorkRunViewResponse } from "@yagni/shared";
import { runUrl } from "@yagni/shared";
import { useRunEvents } from "../hooks/useRunEvents.js";
import { captureUiError } from "../sentry.js";
import { CaseFold } from "./CaseFold.js";
import { ClampText } from "./ClampText.js";
import { LiveTalk } from "./LiveTalk.js";
import { Fold, hhmm } from "./LiveStream.js";
import { runCommits } from "./RunSurface.js";
import { caseRunStatusLabel, formatMillicents } from "./useWorkItemRuns.js";
import { useShowsRunCost } from "../hooks/useShowsRunCost.js";
import { runLabel } from "./runLabel.js";
import {
  ATTEMPT_CANDIDATE_KEPT,
  ATTEMPT_LABEL,
  ATTEMPT_LOG_EMPTY,
  ATTEMPT_LOG_LABEL,
  ATTEMPT_LOG_UNREADABLE,
  ATTEMPT_NO_PR,
  ATTEMPT_NOTHING_PRODUCED,
  ATTEMPT_OUTPUTS_UNREADABLE,
  ATTEMPT_PRODUCED_LABEL,
  ATTEMPT_RECORD_LINK,
  ATTEMPT_STEPS_LABEL,
  attemptStepsFact,
} from "./workCopy.js";
import "./liveStream.css";

/** The attempt's outputs, from the run view, when the view is about this run. */
export function attemptOutputsOf(view: WorkRunViewResponse, runId: string | null) {
  const own = runId != null && view.run?.id === runId;
  const outputs = own ? (view.run?.artifacts ?? []) : [];
  return {
    own,
    steps: own ? view.steps : [],
    tracked: own && view.stepSource === "mission",
    candidate: outputs.find((output) => output.kind === "code_candidate") ?? null,
    pr: outputs.find((output) => output.kind === "pr") ?? null,
    outputs,
  };
}

/**
 * The attempt's pull request, in one word: the item's own PR, else one the
 * run recorded, else "no PR". Null when the outputs could not be read, so a
 * failed read never claims the attempt opened nothing.
 */
export function prWordOf(
  prNumber: number | null | undefined,
  pr: { prNumber?: number | null } | null,
  outputsKnown: boolean,
): string | null {
  if (prNumber != null) return `PR #${prNumber}`;
  if (!outputsKnown) return null;
  if (pr?.prNumber != null) return `PR #${pr.prNumber}`;
  if (pr) return "PR opened";
  return ATTEMPT_NO_PR;
}

/** The PR word on the produced line: a link to the pull request when the item knows where it is. */
function ProducedPr({ prWord, prUrl }: { prWord: string; prUrl: string | null }) {
  if (prWord === ATTEMPT_NO_PR) return <>no PR opened</>;
  if (!prUrl) return <>{prWord}</>;
  return (
    <a href={prUrl} target="_blank" rel="noreferrer" data-testid="attempt-pr-link">
      {prWord}
    </a>
  );
}

export function AttemptBlock({
  workItemId,
  run,
  runs = [],
  view,
  live,
  workerName,
  prNumber,
  prUrl = null,
  defaultOpen = false,
  viewFailed = false,
  label = ATTEMPT_LABEL,
}: {
  workItemId: string;
  /** The attempt, from the runs roll-up. Null renders nothing. */
  run: WorkItemRunListEntry | null;
  /** Every run on the item, so a repeat is named by why it repeated. */
  runs?: readonly WorkItemRunListEntry[];
  view: WorkRunViewResponse;
  /** The attempt is still running: the log keeps polling. */
  live: boolean;
  workerName: string | null;
  /** The item's pull request, when the attempt opened one. */
  prNumber?: number | null;
  /** Where that pull request is; the produced line links there. */
  prUrl?: string | null;
  /** Open on arrival: a stop that recorded no reason has only this to show. */
  defaultOpen?: boolean;
  /** The run view could not be read: what the attempt kept is unknown, not empty. */
  viewFailed?: boolean;
  /** The fold's label: "This run", or "Earlier run" when another run is the one the sheet follows. */
  label?: string;
}) {
  const runId = run?.id ?? null;
  const [logFailed, setLogFailed] = useState(false);
  useEffect(() => setLogFailed(false), [runId]);
  // An empty id disables the read: with no run named, the hook would fetch
  // every attempt's events, which is the bug this block exists to fix.
  const events = useRunEvents(run ? workItemId : "", {
    runId,
    live,
    onError: (error) => {
      setLogFailed(true);
      captureUiError(error, { area: "Work", operation: "read_attempt_events" });
    },
  });
  const showsCost = useShowsRunCost();
  if (!run) return null;

  const { steps, tracked, candidate, pr, outputs } = attemptOutputsOf(view, run.id);
  const commits = runCommits(events);
  const done = steps.filter((step) => step.state === "done").length;
  const prWord = prWordOf(prNumber, pr, !viewFailed);
  // Null on the pull-request plan: the summary line drops the charge.
  const charge = showsCost ? formatMillicents(run.sellMillicents) : null;
  const summary = [
    attemptStepsFact(done, steps.length, tracked),
    candidate ? ATTEMPT_CANDIDATE_KEPT : null,
    prWord,
    charge,
  ]
    .filter(Boolean)
    .join(" · ");
  const started = hhmm(run.startedAt);
  const finished = live ? null : hhmm(run.finishedAt);
  const meta = [
    runLabel(run, runs),
    started && finished ? `${started} to ${finished}` : started,
    caseRunStatusLabel(run),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <CaseFold
      label={label}
      summary={summary}
      meta={meta}
      defaultOpen={defaultOpen}
      testId="case-file-attempt"
      className="cf-attempt"
    >
      {steps.length > 0 && (
        <div className="cf-attempt-row" data-testid="attempt-steps">
          <div className="cf-attempt-lab">
            {ATTEMPT_STEPS_LABEL}
            <span className="cf-attempt-lab-rest">
              {tracked ? `${done} of ${steps.length} done` : `${steps.length}`}
            </span>
          </div>
          <ol className="wk-run-steps cf-attempt-steps">
            {steps.map((step) => (
              <li
                key={step.sequence}
                className={`wk-run-step is-${step.state.replace("_", "-")}`}
                data-state={step.state}
              >
                <span className="wk-run-step-mark" aria-hidden>
                  {tracked && step.state === "done" ? "✓" : step.sequence}
                </span>
                <ClampText lines={1} className="wk-run-step-body">
                  <span className="wk-run-step-desc">{step.description}</span>
                </ClampText>
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="cf-attempt-row" data-testid="attempt-produced">
        <div className="cf-attempt-lab">{ATTEMPT_PRODUCED_LABEL}</div>
        <p className="cf-attempt-produced">
          {viewFailed
            ? ATTEMPT_OUTPUTS_UNREADABLE
            : [
                ...outputs
                  .filter((output) => output.kind !== "pr" && output.kind !== "sandbox")
                  .map((output) => output.label),
                commits.length > 0
                  ? `${commits.length} commit${commits.length === 1 ? "" : "s"} on ${commits[commits.length - 1]!.branch}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || ATTEMPT_NOTHING_PRODUCED}
          {prWord && (
            <>
              {" · "}
              <ProducedPr prWord={prWord} prUrl={prUrl} />
            </>
          )}
        </p>
      </div>
      <div className="ls-more cf-attempt-more">
        <Fold
          testId="attempt-log"
          word={ATTEMPT_LOG_LABEL}
          rest={`${events.length} event${events.length === 1 ? "" : "s"}`}
          cap=""
        >
          {events.length > 0 ? (
            <LiveTalk events={events} workerName={workerName} live={live} />
          ) : (
            <p className="cf-attempt-empty" data-testid="attempt-log-empty">
              {logFailed ? ATTEMPT_LOG_UNREADABLE : ATTEMPT_LOG_EMPTY}
            </p>
          )}
        </Fold>
      </div>
      <RouterLink
        className="is-quiet cf-attempt-link"
        to={runUrl(workItemId, run.attemptNo)}
        data-testid="attempt-record-link"
      >
        {ATTEMPT_RECORD_LINK} →
      </RouterLink>
    </CaseFold>
  );
}
